import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AlertComponent } from '@principle-theorem/ng-shared';
import { Address, Brand } from '@principle-theorem/principle-core';
import { type IContactDetails } from '@principle-theorem/principle-core/interfaces';
import { filterUndefined } from '@principle-theorem/shared';
import { map } from 'rxjs/operators';
import { MIN_PHONE_NUMBER_LENGTH } from '../../forms/custom-validators';
import { OrganisationService } from '../../organisation.service';
import { AddressInputComponent } from '../address-input/address-input.component';
import { ContactFormGroup } from './contact.formgroup';
import { TagsInputComponent } from '../tags-input/tags-input.component';
import { TagType } from '../../store/global-store.service';

@Component({
  selector: 'pr-contact-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    AlertComponent,
    AddressInputComponent,
    TagsInputComponent,
  ],
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactFormComponent {
  private _organisation = inject(OrganisationService);
  contact = input<IContactDetails | undefined>(undefined);
  submitted = output<IContactDetails>();
  minPhoneLength = MIN_PHONE_NUMBER_LENGTH;
  tagType = TagType.Contact;
  form = new ContactFormGroup();

  tagCol = toSignal(
    this._organisation.brand$.pipe(
      filterUndefined(),
      map((brand) => Brand.contactTagCol(brand))
    )
  );

  constructor() {
    toObservable(this.contact)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((contact) => {
        this.form.patchValue(
          { ...contact, address: Address.normaliseAddress(contact) },
          { emitEvent: false }
        );
      });
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value;
    const contact = this.contact();

    this.submitted.emit({
      ...data,
      address: data.address.address ?? '',
      metadata: { ...contact?.metadata, address: data.address },
    });
  }
}
